import React from 'react';
import { Timestamp } from 'firebase/firestore';
import { User, signOut, onAuthStateChanged } from 'firebase/auth';

import { api } from 'api';
import { IAdmin } from 'types';
import { castDoc } from 'utils/firebase';
import { firebaseAuth } from 'config/firebase';
import { CURRENT_ADMIN } from 'constants/mocks';
import { IS_MOCKED } from 'constants/constants';
import { useAppDispatch, useAppSelector } from 'store';
import { selectCurrentUser, setCurrentUser } from 'store/user';

import { useToast } from '../use-toast';

export const useCurrentUser = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const { handleFirebaseError, showError } = useToast();

  const [loading, setLoading] = React.useState(true);

  const handleAuthStateChanged = async (user: User | null) => {
    try {
      setLoading(true);

      if (currentUser && user && currentUser.id === user.uid) {
        return;
      }

      if (!currentUser && user) {
        const response = await api.admins.get(user.uid);

        const document = response.exists()
          ? castDoc<IAdmin>(response)
          : undefined;

        if (!document) {
          await signOut(firebaseAuth);
          showError('Permissions denied. Contact Super Admin');
          return;
        }

        if (document.isActive === false) {
          await signOut(firebaseAuth);
          showError('You account has been deactivated. Contact Super Admin.');
          return;
        }

        dispatch(setCurrentUser(document));
        const lastSessionAt = Timestamp.now();
        await api.admins.update(document.id, { lastSessionAt });
      }
    } catch (error) {
      handleFirebaseError(error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const subscribe = IS_MOCKED
      ? undefined
      : onAuthStateChanged(firebaseAuth, handleAuthStateChanged);

    return () => {
      if (subscribe) subscribe();
    };
  }, []);

  React.useEffect(() => {
    if (IS_MOCKED) {
      dispatch(setCurrentUser(CURRENT_ADMIN));
      setLoading(false);
    }
  }, []);

  return { loading, currentUser } as const;
};
